import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import NewsletterSignup from "../components/NewsletterSignup";
import { api } from "../lib/api";
import { useSEO } from "../lib/seo";
import {
  Wrench, Lightning, Broom, Hammer, Truck, Tree, PaintBrush, Toolbox,
  Ruler, ThermometerSimple, Books, PawPrint, Star, MapPin, ShieldCheck, ArrowRight,
} from "@phosphor-icons/react";

const ICONS = { Wrench, Lightning, Broom, Hammer, Truck, Tree, PaintBrush, Toolbox, Ruler, ThermometerSimple, Books, PawPrint };

function prettify(slug) {
  return slug.split("-").map((s) => s.charAt(0).toUpperCase() + s.slice(1)).join(" ");
}

export default function LocationLanding() {
  const { category: catSlug, city: citySlug } = useParams();
  const [workers, setWorkers] = useState([]);
  const [categoryMeta, setCategoryMeta] = useState(null);
  const cityName = prettify(citySlug || "");

  useEffect(() => {
    api.get("/categories").then((r) => {
      const found = r.data.find((c) => c.id === catSlug);
      setCategoryMeta(found || null);
    });
    api.get("/workers", { params: { category: catSlug, city: cityName } }).then((r) => setWorkers(r.data));
  }, [catSlug, citySlug, cityName]);

  const title = categoryMeta
    ? `${categoryMeta.name} in ${cityName} — Hire Trusted Local Pros`
    : `Local Pros in ${cityName}`;

  const description = categoryMeta
    ? `Find vetted, top-rated ${categoryMeta.name.toLowerCase()} professionals in ${cityName}. Get quotes, chat, and hire trusted Canadian pros on CanHireSolution.com.`
    : `Hire verified local skilled workers in ${cityName} on CanHireSolution.com.`;

  useSEO({
    title,
    description,
    path: `/services/${catSlug}/${citySlug}`,
    jsonLd: categoryMeta ? {
      "@context": "https://schema.org",
      "@type": "Service",
      serviceType: categoryMeta.name,
      areaServed: { "@type": "City", name: cityName, addressCountry: "CA" },
      provider: { "@type": "Organization", name: "CanHireSolution.com", url: "https://canhiresolution.com" },
      description,
    } : null,
  });

  const Ico = (categoryMeta && ICONS[categoryMeta.icon]) || Wrench;

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <section className="pt-24 pb-12 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 mb-3 flex items-center gap-2">
          <Link to="/" className="hover:text-slate-900">Home</Link> /
          <Link to="/workers" className="hover:text-slate-900">Workers</Link> /
          <span>{categoryMeta?.name || prettify(catSlug)}</span> /
          <span>{cityName}</span>
        </div>
        <div className="flex items-start gap-4 mb-6">
          <div className="w-16 h-16 bg-brand text-white grid place-items-center flex-shrink-0">
            <Ico size={32} weight="bold" />
          </div>
          <div>
            <h1 className="font-display text-4xl sm:text-5xl tracking-tight">
              {categoryMeta?.name || prettify(catSlug)} <span className="text-brand">in {cityName}</span>
            </h1>
            <p className="text-base text-slate-600 mt-2 max-w-2xl">{description}</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-6 text-sm text-slate-700 mb-10">
          <span className="flex items-center gap-1"><ShieldCheck size={16} className="text-brand" /> Verified pros</span>
          <span className="flex items-center gap-1"><Star size={16} weight="fill" className="text-amber-500" /> Real reviews</span>
          <span className="flex items-center gap-1"><MapPin size={16} className="text-accent-red" /> Serves {cityName}</span>
        </div>

        <h2 className="font-display text-2xl mb-4">Available {categoryMeta?.name.toLowerCase() || "pros"} in {cityName}</h2>
        {workers.length === 0 ? (
          <div className="border border-dashed border-slate-300 p-12 text-center">
            <p className="text-slate-600 mb-4">No {(categoryMeta?.name || "pros").toLowerCase()} listed in {cityName} yet — be the first.</p>
            <Link to="/login" className="inline-block bg-brand text-white px-5 py-3 font-semibold">Join as a Pro →</Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {workers.map((w) => (
              <Link key={w.user_id} to={`/workers/${w.user_id}`} className="border border-slate-200 p-5 hover:-translate-y-1 hover:shadow-md transition-all">
                <div className="flex items-start gap-3 mb-3">
                  {w.picture ? <img src={w.picture} alt={w.name} className="w-12 h-12 object-cover" /> : <div className="w-12 h-12 bg-brand text-white grid place-items-center font-display">{w.name?.[0]}</div>}
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-lg truncate flex items-center gap-1">
                      {w.name}
                      {w.verification?.status === "verified" && <ShieldCheck size={14} weight="fill" className="text-emerald-600 flex-shrink-0" />}
                    </div>
                    <div className="text-xs text-slate-500 truncate">{w.headline}</div>
                  </div>
                </div>
                <p className="text-sm text-slate-700 line-clamp-2 mb-3">{w.bio}</p>
                <div className="flex items-center justify-between text-sm">
                  <span className="font-semibold">${w.hourly_rate}/hr</span>
                  <span className="flex items-center gap-1"><Star size={14} weight="fill" className="text-amber-500" /> {w.rating?.toFixed?.(1) || "—"}</span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      <section className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <h2 className="font-display text-3xl mb-4">Hiring {(categoryMeta?.name || "a pro").toLowerCase()} in {cityName}: what to know</h2>
          <div className="prose prose-slate max-w-none">
            <p>
              When hiring a {(categoryMeta?.name || "service provider").toLowerCase()} in {cityName}, look for licensed and insured pros with verified reviews.
              CanHireSolution.com makes this easy — every Verified Pro on our platform has submitted credentials reviewed by our team.
            </p>
            <ul>
              <li><strong>Get multiple quotes:</strong> Post a job and let pros come to you with competitive bids.</li>
              <li><strong>Check reviews:</strong> Real customers leave real reviews. Look at both the rating and the comments.</li>
              <li><strong>Verified badge:</strong> Look for the green shield — it means we&apos;ve checked their license and insurance.</li>
              <li><strong>Chat first:</strong> Use our in-app chat to ask questions before committing.</li>
            </ul>
            <p>
              Whether you need urgent help or are planning a larger project in {cityName}, CanHireSolution.com connects you with skilled, trusted Canadians fast.
            </p>
          </div>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="bg-slate-900 text-white p-8 md:p-12">
          <NewsletterSignup variant="dark" source={`location-${catSlug}-${citySlug}`} />
        </div>
      </section>

      <Footer />
    </div>
  );
}
